import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setToken, getToken } from "../lib/api";
import { setActiveBrand } from "@/lib/brandProfile";

// Auto-apply the ENSO Automotive Ferrari-red palette when Mikey logs in so
// he lands on his own branding without having to touch the Magic Brand
// Switch (that switch is Steve's pitch tool, not Mikey's config). Deliberately
// narrow — we don't want to auto-brand every workshop on login (would break
// the RRG boardroom reveal choreography), just tenants where the workshop
// has explicitly requested a persistent brand.
function applyWorkshopBrand(u) {
  if (!u) return;
  const ws = (u.workshop_name || "").toLowerCase();
  // Always resolve on login: branded demo tenants get their own skin;
  // everyone else (Steve, Arran, Dave, Clive, real customers) starts on the
  // default ServiceSell PRO dark+green — wiping any skin left on this device
  // by a previous login. Steve's Magic Brand Switch still cycles from there.
  let key = "default";
  if (ws.includes("enso")) key = "enso";
  else if (ws.includes("hyundai")) key = "hyundai";
  else if (ws.includes("geely")) key = "geely";
  else if (ws.includes("omoda") || ws.includes("jaecoo")) key = "omoda";
  else if (ws.includes("vehicle visuals")) key = "vv";
  else if (ws.includes("toyota")) key = "toyota";
  setActiveBrand(key);
  window.dispatchEvent(new Event("ssp-brand-changed"));
}

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        applyWorkshopBrand(r.data);
      })
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setToken(r.data.token);
    // Fetch the full /me profile so flags like platform_owner are available
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      applyWorkshopBrand(me.data);
      return me.data;
    } catch {
      setUser(r.data.user);
      applyWorkshopBrand(r.data.user);
      return r.data.user;
    }
  };
  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    setToken(r.data.token);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      applyWorkshopBrand(me.data);
      return me.data;
    } catch {
      setUser(r.data.user);
      applyWorkshopBrand(r.data.user);
      return r.data.user;
    }
  };
  const logout = () => {
    setToken(null);
    setUser(null);
    // Reset white-label brand to default so the next person logging in (or
    // the same person on a fresh demo) doesn't see a stuck RRG/Toyota skin.
    setActiveBrand("default");
    window.dispatchEvent(new Event("ssp-brand-changed"));
  };
  const launchDemoTenant = async () => {
    const r = await api.post("/auth/demo-tenant", {});
    setToken(r.data.token);
    // Seed the Jane Driver job inside the brand-new tenant so the dashboard
    // is rich the moment the dealer principal lays eyes on it.
    try { await api.post("/jobs/demo"); } catch { /* non-fatal */ }
    const me = await api.get("/auth/me");
    setUser(me.data);
    return { user: me.data, credentials: r.data.demo_credentials };
  };
  const launchToyotaDemo = async () => {
    const r = await api.post("/auth/demo-tenant-rrg", {});
    setToken(r.data.token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    return { user: me.data, credentials: r.data.demo_credentials };
  };

  // Backwards-compat alias — some pages still import launchRrgDemo
  const launchRrgDemo = launchToyotaDemo;

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, launchDemoTenant, launchToyotaDemo, launchRrgDemo }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
